import React, { useEffect, useState, useCallback } from 'react';
import AdminImagePicker from '../AdminImagePicker';

export default function BlogCategoriesPanel({ API, adminToken, isDarkMode, showNotification }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState({ slug: '', name: '', icon: '✈', image_url: '', description: '', display_order: 0 });
  // pickerTarget: null | { type: 'new' } | { type: 'row', slug }
  const [pickerTarget, setPickerTarget] = useState(null);
  // confirmDelete: null | the category object pending deletion
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/blog/admin/categories`, { headers });
    if (res.ok) setCats(await res.json());
    setLoading(false);
  }, [API, adminToken]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const createCat = async () => {
    if (!newCat.slug || !newCat.name) {
      showNotification?.('slug and name required', 'error');
      return;
    }
    const form = new FormData();
    form.append('slug', newCat.slug);
    form.append('name', newCat.name);
    form.append('icon', newCat.icon || '✈');
    form.append('image_url', newCat.image_url || '');
    form.append('description', newCat.description || '');
    form.append('display_order', String(newCat.display_order));
    const res = await fetch(`${API}/blog/admin/categories`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (res.ok) {
      showNotification?.('Category added', 'success');
      setNewCat({ slug: '', name: '', icon: '✈', image_url: '', description: '', display_order: cats.length + 1 });
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      showNotification?.(`Add failed: ${err.detail || res.statusText}`, 'error');
    }
  };

  const updateCat = async (slug, patch) => {
    const form = new FormData();
    Object.entries(patch).forEach(([k, v]) => form.append(k, String(v)));
    const res = await fetch(`${API}/blog/admin/categories/${slug}`, {
      method: 'PUT',
      headers,
      body: form,
    });
    if (res.ok) load();
    else showNotification?.('Update failed', 'error');
  };

  // Triggered by the row "Delete" link — opens the in-page confirmation modal
  const askDeleteCat = (cat) => setConfirmDelete(cat);

  // Actually perform the delete (called by the modal's confirm button)
  const performDeleteCat = async () => {
    if (!confirmDelete) return;
    const slug = confirmDelete.slug;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/blog/admin/categories/${slug}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        showNotification?.('Category deleted', 'success');
        setConfirmDelete(null);
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification?.(`Delete failed: ${err.detail || res.statusText}`, 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inpBg = isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const rowBg = isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50';

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold">Categories</h2>
        <p className={`text-sm mt-0.5 ${muted}`}>Topics shown as pills on the list page (✓ GREETINGS, ✓ SOUK, etc.) and as the rubber-stamp badge on each boarding-pass card.</p>
      </div>

      {/* New category form */}
      <div className={`p-4 rounded-lg border ${border} mb-5 ${isDarkMode ? 'bg-gray-900/30' : 'bg-gray-50'}`}>
        <div className="text-xs uppercase tracking-wider font-bold mb-3 opacity-70">+ Add new category</div>
        <div className="grid grid-cols-1 md:grid-cols-8 gap-3">
          <input
            placeholder="slug (e.g. greetings)"
            value={newCat.slug}
            onChange={e => setNewCat({ ...newCat, slug: e.target.value })}
            className={`px-3 py-2 rounded border text-sm font-mono ${inpBg} md:col-span-1`}
          />
          <input
            placeholder="Name (e.g. Greetings)"
            value={newCat.name}
            onChange={e => setNewCat({ ...newCat, name: e.target.value })}
            className={`px-3 py-2 rounded border text-sm ${inpBg} md:col-span-1`}
          />
          <input
            placeholder="Icon (emoji fallback)"
            value={newCat.icon}
            onChange={e => setNewCat({ ...newCat, icon: e.target.value })}
            className={`px-3 py-2 rounded border text-sm ${inpBg} text-center`}
            maxLength={6}
          />
          <div className="md:col-span-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPickerTarget({ type: 'new' })}
              className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold whitespace-nowrap"
            >
              📁 Pick image
            </button>
            {newCat.image_url ? (
              <>
                <img src={newCat.image_url} alt="" className="w-8 h-8 rounded border object-cover" />
                <button
                  type="button"
                  onClick={() => setNewCat({ ...newCat, image_url: '' })}
                  className="text-xs text-red-500 hover:underline"
                  title="Clear image"
                >
                  clear
                </button>
              </>
            ) : (
              <span className={`text-xs ${muted}`}>no image · uses emoji fallback</span>
            )}
          </div>
          <input
            placeholder="Description (one-line tagline)"
            value={newCat.description}
            onChange={e => setNewCat({ ...newCat, description: e.target.value })}
            className={`px-3 py-2 rounded border text-sm ${inpBg} md:col-span-3`}
          />
          <button
            onClick={createCat}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold"
          >
            + Add
          </button>
          <input
            type="number"
            placeholder="Order"
            value={newCat.display_order}
            onChange={e => setNewCat({ ...newCat, display_order: parseInt(e.target.value || '0', 10) })}
            className={`px-3 py-2 rounded border text-sm ${inpBg} md:col-span-1`}
          />
        </div>
      </div>

      {loading ? (
        <div className={`text-center py-10 ${muted}`}>Loading…</div>
      ) : cats.length === 0 ? (
        <div className={`text-center py-12 ${muted}`}>No categories yet — add one above.</div>
      ) : (
        <div className={`rounded-lg border ${border} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead className={isDarkMode ? 'bg-gray-900/60' : 'bg-gray-100'}>
              <tr className="text-left">
                <th className="px-3 py-3 font-bold">Slug</th>
                <th className="px-3 py-3 font-bold w-16 text-center">Icon</th>
                <th className="px-3 py-3 font-bold w-36 text-center">Image (overrides icon)</th>
                <th className="px-3 py-3 font-bold">Name</th>
                <th className="px-3 py-3 font-bold">Description (tagline)</th>
                <th className="px-3 py-3 font-bold w-20">Order</th>
                <th className="px-3 py-3 font-bold w-16 text-center">Active</th>
                <th className="px-3 py-3 font-bold text-right w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.slug} className={`border-t ${border} ${rowBg}`}>
                  <td className="px-3 py-3 font-mono text-xs">{c.slug}</td>
                  <td className="px-3 py-3 text-center">
                    <input
                      defaultValue={c.icon || '✈'}
                      onBlur={e => { if (e.target.value !== (c.icon || '✈')) updateCat(c.slug, { icon: e.target.value || '✈' }); }}
                      className={`px-2 py-1 rounded border text-base ${inpBg} w-14 text-center`}
                      maxLength={6}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {c.image_url ? (
                        <img src={c.image_url} alt="" className="w-10 h-10 rounded border object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded border border-dashed flex items-center justify-center text-[10px] opacity-50">none</div>
                      )}
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => setPickerTarget({ type: 'row', slug: c.slug })}
                          className="text-[11px] text-blue-500 hover:underline"
                        >
                          {c.image_url ? 'Change' : 'Pick'}
                        </button>
                        {c.image_url && (
                          <button
                            type="button"
                            onClick={() => updateCat(c.slug, { image_url: '' })}
                            className="text-[11px] text-red-500 hover:underline"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <input
                      defaultValue={c.name}
                      onBlur={e => { if (e.target.value !== c.name) updateCat(c.slug, { name: e.target.value }); }}
                      className={`px-2 py-1 rounded border text-sm ${inpBg} w-full`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      defaultValue={c.description || ''}
                      placeholder="One-line tagline for the home card"
                      onBlur={e => { if (e.target.value !== (c.description || '')) updateCat(c.slug, { description: e.target.value }); }}
                      className={`px-2 py-1 rounded border text-sm ${inpBg} w-full`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      type="number"
                      defaultValue={c.display_order}
                      onBlur={e => { const v = parseInt(e.target.value || '0', 10); if (v !== c.display_order) updateCat(c.slug, { display_order: v }); }}
                      className={`px-2 py-1 rounded border text-sm ${inpBg} w-16`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!c.is_active}
                      onChange={e => updateCat(c.slug, { is_active: e.target.checked })}
                      className="w-5 h-5"
                    />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      onClick={() => askDeleteCat(c)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Image Manager picker — opens when admin clicks Pick / Change on a category.
          AdminImagePicker internally calls `${API}/api/admin/image-folders`, so we must
          pass the backend ORIGIN (without trailing /api), not the API value the rest of
          this panel uses. */}
      <AdminImagePicker
        open={!!pickerTarget}
        onClose={() => setPickerTarget(null)}
        onPick={(asset) => {
          const url = asset?.url || '';
          if (!url) return;
          if (pickerTarget?.type === 'new') {
            setNewCat(prev => ({ ...prev, image_url: url }));
          } else if (pickerTarget?.type === 'row') {
            updateCat(pickerTarget.slug, { image_url: url });
          }
          setPickerTarget(null);
        }}
        API={API.replace(/\/api\/?$/, '')}
        authHeaders={headers}
        title="Pick a category image"
      />

      {/* Delete-category confirmation modal — replaces the native browser confirm() */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => { if (!deleting) setConfirmDelete(null); }}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${
              isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="w-10 h-10 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center text-xl font-bold">⚠</div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delete this category?</h3>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This cannot be undone.</p>
              </div>
            </div>

            {/* Body — show the category being deleted */}
            <div className="p-5">
              <div className={`flex items-center gap-3 p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                {confirmDelete.image_url ? (
                  <img src={confirmDelete.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border" />
                ) : (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                    {confirmDelete.icon || '✈'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{confirmDelete.name}</div>
                  <div className={`text-xs font-mono mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>/{confirmDelete.slug}</div>
                </div>
              </div>
              <p className={`text-sm mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                If any published posts still belong to this category, the deletion will be blocked and you'll need to reassign them first.
              </p>
            </div>

            {/* Footer buttons */}
            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className={`px-4 py-2 rounded-lg text-sm font-bold ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={performDeleteCat}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : '🗑 Delete category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
